import { LinkField, NavigationElement, NavigationHighlightLinkGroup } from '@tmh-website/shared/cms';
import { LocalePrefix } from '@tmh-website/shared/const';

/**
 * This function will treat 3 cases:
 * 1. The current link is the same as the link value, then it will return true
 * 2. The current link is a child of the link value, then it will return true
 * 2.1. If there are multiple matching items, that's the exception we need handle by remove the parent on the same level
 * 3. Otherwise, it will return false
 *
 * @param pagePath the current link of the page
 * @param linkPath the link value given from the backend: visibleHighlightLinkGroups / NavigationElement
 * @param visibleLinksPaths the visible links from the backend could be visibleHighlightLinkGroups for menu / NavigationElement for sidebar/burger menu
 * @param locale the locale
 * @param type the type of the data, could be 'category-link' (NavigationElement) or 'navigation-link' (visibleHighlightLinkGroups)
 * @returns
 */
export const determineLinkState = (
  pagePath: string,
  linkPath: string,
  visibleLinksPaths: NavigationHighlightLinkGroup[] | NavigationElement[],
  locale: LocalePrefix,
  type: 'category-link' | 'navigation-link'
): boolean => {
  // Case 1: Exact match, selected
  if (pagePath === linkPath) return true;

  // Case 2: Child selected - we need to remove the parent on the same level and remove the html for the shop
  if (pagePath.includes(linkPath.replace(/\.html$/, ''))) {
    const matchingItems = findMatchingItemsExcludingParent(
      pagePath,
      type === 'category-link'
        ? transformVisibleLinksPaths(visibleLinksPaths as NavigationElement[])
        : (visibleLinksPaths as NavigationHighlightLinkGroup[]),
      locale
    );

    // If there are matching items, find the longest one
    if (matchingItems.length > 0) {
      const longestMatchingItem = matchingItems.reduce((longest, current) => {
        const longestPage = longest.page as LinkField;
        const currentPage = current.page as LinkField;

        return longestPage.value.length >= currentPage.value.length ? longest : current;
      });

      // If the longest matching item matches the link value, return 'true'
      if ((longestMatchingItem?.page as LinkField)?.value === linkPath) return true;
    }
  }

  return false;
};

/**
 * This function finds matching items excluding parent paths based on the current URL.
 * @param currentUrl The current URL of the visted page
 * @param visibleLinksPaths That is the visible links from the backend could be visibleHighlightLinkGroups for menu / NavigationElement for sidebar/burger menu
 * @param locale the locale is used to define the last part of the URL
 * @returns an array of NavigationHighlightLinkGroup objects that match the current URL, excluding parent paths
 */
const findMatchingItemsExcludingParent = (
  currentUrl: string,
  visibleLinksPaths: NavigationHighlightLinkGroup[],
  locale: LocalePrefix
): NavigationHighlightLinkGroup[] => {
  const matchingItems: NavigationHighlightLinkGroup[] = [];

  // Extract possible parent paths from the current URL
  const urlParts = currentUrl.split('/');
  const localeIndex = urlParts.indexOf(locale);
  const urlPartsToCheck = localeIndex !== -1 ? urlParts.slice(0, localeIndex + 1) : urlParts;
  for (let i = urlPartsToCheck.length - 1; i >= 0; i--) {
    const url = urlPartsToCheck.slice(0, i + 1).join('/');

    // Check if any visibleLinksPaths matches the current URL
    const matchingItem = visibleLinksPaths.find(item => {
      const page = item.page as LinkField;

      return page.value.replace(/\.html$/, '') === url;
    });
    // If a match is found, push it to matchingItems and break the loop
    if (matchingItem) {
      matchingItems.push(matchingItem);
    }
  }

  return matchingItems;
};

/**
 *  This function will cast the type of data NavigationElement to the same structure as NavigationHighlightLinkGroup,
 *  so we can use the same function to determine the link state
 * @param visibleLinksPaths NavigationElement
 * @returns same structure as NavigationHighlightLinkGroup
 */
const transformVisibleLinksPaths = (visibleLinksPaths: NavigationElement[]): NavigationHighlightLinkGroup[] => {
  return visibleLinksPaths.map(item => {
    const id = item.id;
    const page = item.__component === 'navigation.page-link-group' ? item.link.page : (item.page as LinkField);
    return { id, page };
  });
};
